/**
 * PCER Report Utility Functions
 * Helper functions for Port Call Evaluation Report calculations
 */

/**
 * Convert PostgreSQL interval string to days
 * Handles formats like "5 days 12:30:00" or "12:30:00"
 * @param {string} intervalStr - PostgreSQL interval string
 * @returns {number} - Number of days (decimal)
 */
export function intervalToDays(intervalStr) {
  if (!intervalStr) return 0;
  
  try {
    // Parse PostgreSQL interval format
    // Examples: "5 days 12:30:00", "12:30:00", "5 days"
    let totalDays = 0;
    
    // Extract days if present
    const daysMatch = intervalStr.match(/(\d+)\s*days?/i);
    if (daysMatch) {
      totalDays += parseInt(daysMatch[1]);
    }
    
    // Extract time portion (HH:MM:SS)
    const timeMatch = intervalStr.match(/(\d+):(\d+):(\d+)/);
    if (timeMatch) {
      const hours = parseInt(timeMatch[1]);
      const minutes = parseInt(timeMatch[2]);
      const seconds = parseInt(timeMatch[3]);
      totalDays += (hours / 24) + (minutes / 1440) + (seconds / 86400);
    }
    
    return totalDays;
  } catch (error) {
    console.error('Error parsing interval:', intervalStr, error);
    return 0;
  }
}

/**
 * Calculate difference between two dates in days
 * @param {string|Date} endDate - End date
 * @param {string|Date} startDate - Start date
 * @returns {number} - Difference in days (can be negative)
 */
export function dateDiffInDays(endDate, startDate) {
  if (!endDate || !startDate) return null;
  
  try {
    const end = new Date(endDate);
    const start = new Date(startDate);
    const diffMs = end - start;
    return diffMs / (1000 * 60 * 60 * 24);
  } catch (error) {
    console.error('Error calculating date difference:', error);
    return null;
  }
}

/**
 * Format timestamp to readable date string
 * @param {string|Date} timestamp - ISO timestamp
 * @param {string} format - 'short' or 'long'
 * @returns {string} - Formatted date
 */
export function formatDate(timestamp, format = 'short') {
  if (!timestamp) return 'N/A';
  
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    if (format === 'long') {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    // Short format: "Feb 04, 2026"
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting date:', timestamp, error);
    return 'N/A';
  }
}

/**
 * Calculate Lay Can Status based on ATA and laycan dates
 * @param {string|Date} ata - Actual Time of Arrival
 * @param {string|Date} laycanStart - Lay Can Start Date
 * @param {string|Date} laycanEnd - Lay Can End Date (Latest Arrival)
 * @returns {string} - Status: "Early Arrival", "As Planned", "Late Arrival", or "N/A"
 */
export function calculateLaycanStatus(ata, laycanStart, laycanEnd) {
  if (!ata) return 'N/A';
  if (!laycanStart || !laycanEnd) return 'N/A';
  
  try {
    const ataDate = new Date(ata);
    const startDate = new Date(laycanStart);
    const endDate = new Date(laycanEnd);
    
    if (ataDate < startDate) {
      return 'Early Arrival';
    } else if (ataDate >= startDate && ataDate <= endDate) {
      return 'As Planned';
    } else {
      return 'Late Arrival';
    }
  } catch (error) {
    console.error('Error calculating laycan status:', error);
    return 'N/A';
  }
}

/**
 * Format number to fixed decimal places
 * @param {number} value - Numeric value
 * @param {number} decimals - Number of decimal places
 * @returns {string} - Formatted number or 'N/A'
 */
export function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return Number(value).toFixed(decimals);
}

/**
 * Get status color class for Lay Can Status
 * @param {string} status - Laycan status
 * @returns {string} - Tailwind CSS color classes
 */
export function getLaycanStatusColor(status) {
  switch (status) {
    case 'Early Arrival':
      return 'bg-blue-100 text-blue-800';
    case 'As Planned':
      return 'bg-green-100 text-green-800';
    case 'Late Arrival':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Get status color class for Port Stay Deviation
 * @param {number} deviation - Port stay deviation in days
 * @returns {string} - Tailwind CSS color classes
 */
export function getPortStayDeviationColor(deviation) {
  if (deviation === null || deviation === undefined || isNaN(deviation)) {
    return 'text-gray-600';
  }
  
  if (deviation > 0.5) {
    return 'text-red-600 font-semibold'; // Over planned
  } else if (deviation < -0.5) {
    return 'text-blue-600 font-semibold'; // Under planned
  } else {
    return 'text-green-600'; // Close to planned
  }
}

/**
 * Format date for input field (YYYY-MM-DD)
 * @param {Date} date - Date object
 * @returns {string} - Formatted date string
 */
export function formatDateForInput(date) {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get date range preset (e.g., "This Month", "Last 3 Months")
 * @param {string} preset - Preset name
 * @returns {object} - { startDate, endDate }
 */
export function getDateRangePreset(preset) {
  const today = new Date();
  const startDate = new Date();
  const endDate = new Date();
  
  switch (preset) {
    case 'this-month':
      startDate.setDate(1);
      break;
    case 'last-month':
      startDate.setMonth(startDate.getMonth() - 1);
      startDate.setDate(1);
      endDate.setDate(0); // Last day of previous month
      break;
    case 'last-3-months':
      startDate.setMonth(startDate.getMonth() - 3);
      break;
    case 'last-6-months':
      startDate.setMonth(startDate.getMonth() - 6);
      break;
    case 'this-year':
      startDate.setMonth(0);
      startDate.setDate(1);
      break;
    case 'last-year':
      startDate.setFullYear(startDate.getFullYear() - 1);
      startDate.setMonth(0);
      startDate.setDate(1);
      endDate.setFullYear(endDate.getFullYear() - 1);
      endDate.setMonth(11);
      endDate.setDate(31);
      break;
    default:
      // Default: Last 30 days
      startDate.setDate(startDate.getDate() - 30);
  }
  
  return {
    startDate: formatDateForInput(startDate),
    endDate: formatDateForInput(endDate)
  };
}
